import { Trash2, X } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { Button } from "@/app/components/button";

interface WithdrawOfferConfirmToastProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  /** When true, show loading state on confirm button (e.g. while API is in progress). */
  isConfirming?: boolean;
}

export function WithdrawOfferConfirmToast({
  isOpen,
  onClose,
  onConfirm,
  isConfirming = false,
}: WithdrawOfferConfirmToastProps) {
  const handleConfirm = () => {
    onConfirm();
    // Parent closes toast after withdraw completes (or on error)
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/20 z-50"
          />

          {/* Toast - same size as add-to-cart / offer-sent toast */}
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 z-50 mx-auto max-w-[1440px] px-4 sm:px-6 lg:px-8 pb-[max(1.5rem,env(safe-area-inset-bottom))]"
          >
            <div className="bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
              {/* Header */}
              <div className="bg-primary p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <Trash2 className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Retirar oferta?</h3>
                    <p className="text-xs text-white/80">
                      Esta ação não pode ser desfeita
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  aria-label="Fechar"
                  disabled={isConfirming}
                >
                  <X className="w-5 h-5 text-white" />
                </button>
              </div>

              {/* Content */}
              <div className="p-4">
                <p className="text-sm text-muted-foreground mb-4">
                  Tem certeza que deseja retirar esta oferta?
                </p>
                <div className="flex flex-col gap-2">
                  <Button
                    type="button"
                    onClick={handleConfirm}
                    className="w-full"
                    size="lg"
                    variant="destructive"
                    disabled={isConfirming}
                  >
                    {isConfirming ? (
                      <>Retirando…</>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4 mr-2" />
                        Sim, retirar
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    onClick={onClose}
                    variant="outline"
                    className="w-full"
                    size="lg"
                    disabled={isConfirming}
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
